import type { Lot } from "@/features/lots/types";
import { Link } from "react-router-dom";
import { categoryLabel } from "../utils/categories";
import { formatMoney } from "../utils/formatMoney";
import { formatPeriod } from "../utils/formatPeriod";
import type { GroupedCost } from "../utils/groupBy";
import { resolveLotLabel } from "../utils/resolveLotLabel";

type Props = {
  costs: GroupedCost[];
  lots: Lot[];
  lotsLoading?: boolean;
  lotsError?: boolean;
};

export function CostAllocationsList({ costs, lots, lotsLoading, lotsError }: Props) {
  const lotById = new Map(lots.map((l) => [l.id, l]));

  return (
    <ul className="flex flex-col gap-2">
      {costs.map((c) => {
        const label = resolveLotLabel({
          lotId: c.lotId,
          lot: c.lotId ? lotById.get(c.lotId) : undefined,
          isLoading: lotsLoading,
          isError: lotsError,
        });
        return (
          <li
            key={c.sourceEventId}
            className="rounded-2xl border border-app-border bg-app-surface transition-colors hover:bg-app-surface-2"
          >
            <Link
              to={`/app/costos/${c.sourceEventId}`}
              className="block px-4 py-3.5"
              aria-label={`Ver detalle del costo ${categoryLabel(c.category)} ${formatPeriod(c.periodStart, c.periodEnd)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[14px] text-app-ink-1">
                      {categoryLabel(c.category)}
                    </span>
                    <span className="text-app-ink-3 text-xs">·</span>
                    <span className="text-[12px] text-app-ink-3">{label}</span>
                  </div>
                  <div className="mt-1 text-[12px] text-app-ink-3">
                    {formatPeriod(c.periodStart, c.periodEnd)} · {c.animalCount}{" "}
                    {c.animalCount === 1 ? "animal" : "animales"}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-semibold text-[15px] text-app-ink-1 tabular-nums">
                    {formatMoney(c.totalAmount)}
                  </div>
                </div>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
